import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import LegalDisclaimer from './LegalDisclaimer';

const LINK_GROUPS = [
  {
    heading: 'Education',
    links: [
      { name: 'Dashboard', path: '/dashboard' },
      { name: 'Research', path: '/research' },
      { name: 'Screener', path: '/screener' },
      { name: 'News', path: '/news' },
      { name: 'Resources', path: '/resources' },
    ],
  },
  {
    heading: 'Club',
    links: [
      { name: 'Marketplace', path: '/marketplace' },
      { name: 'Paper Trading', path: '/paper' },
      { name: 'Get Pro Access', path: '/pro-access' },
      { name: 'About', path: '/about' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { name: 'Terms & Conditions', path: '/terms' },
      { name: 'Privacy Policy', path: '/privacy' },
    ],
  },
];

/**
 * Dark-luxury site-wide footer for Neon Alpha Investment Club.
 * - Hidden on the public OG-share page (`/s/:id`) which is meant to be embeddable.
 * - Renders the full 4-disclaimer compliance block on every page.
 */
const SiteFooter = () => {
  const { pathname } = useLocation();
  // Hide footer on the embeddable strategy-share page
  if (pathname.startsWith('/s/')) return null;

  return (
    <footer className="relative bg-[#07070d] text-white/70 border-t border-white/5 overflow-hidden">
      {/* Subtle neon glow background */}
      <div
        aria-hidden
        className="absolute inset-x-0 -top-32 h-64 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.08) 35%, transparent 70%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 via-indigo-500 to-purple-600 flex items-center justify-center">
                <span
                  className="text-white font-extrabold text-lg"
                  style={{ textShadow: '0 0 8px rgba(255,255,255,0.6)' }}
                >
                  N
                </span>
              </div>
              <div>
                <p className="text-white font-bold leading-tight">Neon Alpha</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                  Investment Club
                </p>
              </div>
            </div>
            <p className="text-xs text-white/50 leading-relaxed">
              Research. Intelligence. Education. Community. A premium AI-powered investment
              <em> education </em>
              ecosystem.
            </p>
          </div>

          {/* Link Columns */}
          {LINK_GROUPS.map((group) => (
            <div key={group.heading}>
              <h4 className="text-white text-xs font-semibold uppercase tracking-[0.18em] mb-3">
                {group.heading}
              </h4>
              <ul className="space-y-2 text-sm">
                {group.links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-white/60 hover:text-cyan-300 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Full 4-disclaimer block — site-wide on every page */}
        <LegalDisclaimer variant="full" />

        <div className="mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-2">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} Neon Alpha Investment Club. All rights reserved.
          </p>
          <p className="text-[10px] text-white/30">
            Educational platform · Not a hedge fund · Not an investment advisor · Not a broker
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
